import Big from "big.js";
import { useState, useMemo, useLayoutEffect, useCallback } from "react";
import { Modal, useModalContext } from "../common/Modal";
import { TextInput } from "../common/TextInput";
import { gql, useMutation, useSuspenseQuery } from "@apollo/client";
import { BuyFulFillmentModal_BuyOrderFulfillmentMutation, BuyFulFillmentModal_BuyOrderFulfillmentMutationVariables, BuyFulFillmentModal_GetOrderItemsQuery, BuyFulFillmentModal_GetOrderItemsQueryVariables, BuyFulFillmentModal_GetOrderQuery, BuyFulFillmentModal_GetOrderQueryVariables, BuyOrderFulfillmentInput, CreateOrderFulfillmentInput, WithOrderItemInput } from "../../../codegen/graphql";
import { ButtonGroup } from "../common/ButtonGroup";
import { useRequestManager } from "../common/RequestManager";
import handleProcessResult from "../../lib/handleProcessResult";
import handleGraphqlMutationResult from "../../lib/handleGraphqlMutationResult";
import { DimensionsAndWeightPopover } from "../common/DimensionsPopover";
import { sendcloud_packAndCarry_ups } from "../../../common/shipping/FR/sendcloud_packAndCarry_UPS";
import { getShippingServicesForPackage } from "../../../common/shipping/getShippingServicesForPackage";
import { formatCurrency } from "../../utils/currency";
import { SupplierName } from "../../../common/suppliersConfig";
import { SALE_CHANNEL } from "../../../common/salesChannels";
import { getTranslatedValue } from "../common/TranslatedValue";

// CreateFullfillmentModal component for creating
interface BuyFulFillmentModalProps {
  orderId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const BuyFulFillmentModal: React.FC<BuyFulFillmentModalProps> = ({ 
  orderId, 
  onClose, 
  onSuccess,
}) => {
  return (
    <Modal
      onClose={onClose}
      title="Buy Fulfillment"
      width="large"
    >
      <BuyFulFillmentModalContent
        orderId={orderId}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
};

const BuyFulFillmentModalContent: React.FC<BuyFulFillmentModalProps> = ({ 
  orderId, 
  onClose, 
  onSuccess,
}) => {
  const modalContext = useModalContext();
  const { trackRequest } = useRequestManager();

  const [buyOrderFulfillment, { loading: buyLoading }] = useMutation<
    BuyFulFillmentModal_BuyOrderFulfillmentMutation,
    BuyFulFillmentModal_BuyOrderFulfillmentMutationVariables
  >(BUY_FULFILLMENT_MUTATION);

  // Fetch order items
  const { data: orderQuery } = useSuspenseQuery<
    BuyFulFillmentModal_GetOrderQuery,
    BuyFulFillmentModal_GetOrderQueryVariables
  >(BUY_FULFILLMENT_MODAL_GET_ORDER_QUERY, {
    variables: { orderId },
    fetchPolicy: "network-only"
  });
  const order = orderQuery.order;

  if (!order) {
    throw new Error('Order not found');
  }

  const orderItemsData = order.orderProductsVariations;

  // Form state
  const [form, setForm] = useState<BuyOrderFulfillmentInput>(() => {
    const withOrderItems: BuyOrderFulfillmentInput['withOrderItems'] = orderItemsData.map((orderItem) => {
      return {
        orderItemId: Number(orderItem.id),
        quantity: orderItem.quantity,
      };
    });
    let packaging: BuyOrderFulfillmentInput['packaging'] = {
      weight: 0,
      size1: 0,
      size2: 0,
      size3: 0,
    };


    if (orderItemsData.length > 0) {
      const firstItem = orderItemsData[0];
      const firstItemPackaging = firstItem.supplierProductVariation?.packaging;
      
      if (firstItemPackaging) {
        packaging = firstItemPackaging;
      }
    }

    let defaultFulfillmentPlatform = 'sendcloud';
    if (
      orderItemsData.every((item) => (
        item.supplierProductVariation!.supplierName === 'aliexpress'
      ))
    ) {
      defaultFulfillmentPlatform = 'aliexpress';
    }
    else {
      defaultFulfillmentPlatform = 'sendcloud';
    }

    return {
      ourOrderId: Number(orderId),
      fullfilledPlatform: defaultFulfillmentPlatform,
      packaging,
      withOrderItems,
    };
  });

  // Form event handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    handleChange(name, value);
  }, []);

  const handleChange = useCallback((name: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleLineItemSelection = useCallback((itemId: string) => {
    setForm(prev => {
      const isAlreadySelected = prev.withOrderItems.some(withOrderItem => withOrderItem.orderItemId === Number(itemId));
      if (isAlreadySelected) {
        return {
          ...prev,
          withOrderItems: prev.withOrderItems.filter(withOrderItem => (
            withOrderItem.orderItemId !== Number(itemId)
          ))
        };
      }
      else {
        const quantityInOrder = orderItemsData.find(item => item.id === itemId)!.quantity || 0;
        return {
          ...prev,
          withOrderItems: [...prev.withOrderItems, { orderItemId: Number(itemId), quantity: quantityInOrder }]
        };
      }
    });
  }, []);

  const handleLineItemQuantityChange = useCallback((itemId: string, quantity: number) => {
    setForm(prev => {
      const existingWithOrderItem = prev.withOrderItems.find(withOrderItem => withOrderItem.orderItemId === Number(itemId));
      if (!existingWithOrderItem) {
        return {
          ...prev,
          withOrderItems: [...prev.withOrderItems, { orderItemId: Number(itemId), quantity: quantity }]
        };
      }
      else {
        return {
          ...prev,
          withOrderItems: prev.withOrderItems.map(withOrderItem => (
            withOrderItem.orderItemId === Number(itemId)
              ? { ...withOrderItem, quantity: quantity }
              : withOrderItem
          ))
        };
      }
    });
  }, []);

  // Submitting the fulfillment
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // TODO: Validate form
    // Create new fulfillment
    trackRequest(
      buyOrderFulfillment({
        variables: {
          input: form
        }
      })
        .then(handleGraphqlMutationResult)
        .then((data) => handleProcessResult(data.buyOrderFulfillment.process)),
      {
        title: 'Buying fulfillment',
        isBlocking: true,
      }
    );
  };

  // Set actions for the modal
  useLayoutEffect(() => {
    modalContext.setActions([
      {
        label: "Cancel",
        onClick: onClose,
        preset: "white"
      },
      {
        label: "Buy",
        onClick: handleSubmit,
        disabled: buyLoading,
      }
    ]);
  }, [onClose, handleSubmit, modalContext]);

  // Helper function to display variation details
  const getVariationDescription = (variation: any) => {
    if (!variation) return "Unknown variation";
    
    const variationAttributes = variation.variationTypeValues
      .map((tv: any) => `${getTranslatedValue(tv.type.type)}: ${getTranslatedValue(tv.value)}`)
      .join(", ");
      
    return `${getTranslatedValue(variation.ad.title)} (${variationAttributes})`;
  };

  const isDropship = form.fullfilledPlatform === 'aliexpress';

  const shippingCarrierOptions = useMemo(() => {
    if (form.fullfilledPlatform === 'sendcloud') {
      return [
        {
          identifier: 'ups',
          label: 'UPS Pack and Carry',
        },
      ]
    }
  }, [form.fullfilledPlatform]);

  const estimatedCost = useMemo(() => {
    if (form.fullfilledPlatform === 'sendcloud') {
      const shippingServices = getShippingServicesForPackage({
        onlyServices: [sendcloud_packAndCarry_ups],
        packageWeight: form.packaging!.weight,
        packageSize1: form.packaging!.size1,
        packageSize2: form.packaging!.size2,
        packageSize3: form.packaging!.size3,
        orderPlatform: order.shop.shopType as SALE_CHANNEL,
        origin: 'FR',
        destination: order.customerOriginalAddressCountryCode,
        supplier: SupplierName.FR_ilyes,
      });
      const rate = shippingServices[0]!.rate;
      return new Big(rate.priceWithoutVAT).toNumber();
    }
    return -1;
  }, [form.fullfilledPlatform, form.packaging, order.shop.shopType, order.customerOriginalAddressCountryCode]);

  return (
    <div className="space-y-4">
      {/* Order Type Button Group */}

      <ButtonGroup
        label="Platform"
        name="fullfilledPlatform"
        options={[
          // { value: '', label: 'No platform' },
          { value: 'aliexpress', label: 'AliExpress' },
          { value: 'sendcloud', label: 'SendCloud UPS Pack and Carry' },
        ]}
        value={form.fullfilledPlatform}
        onChange={(value) => handleChange('fullfilledPlatform', value)}
      />

      {/* Packaging Information, only for in-stock fulfillment */}
      {isDropship === false && (
          <div className="grid grid-cols-4 gap-4">
          
            <DimensionsAndWeightPopover
              dimensionsAndWeight={form.packaging!}
              onChange={(dimensionsAndWeight) => handleChange('packaging', dimensionsAndWeight)}
            />
        </div>
      )}

      {form.fullfilledPlatform === 'sendcloud' && (
        <p className="text-lg text-gray-900 mb-4">
          Estimated cost:  {formatCurrency(estimatedCost)} HT
        </p>
      )}
      
      {/* Order Items Selection */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Order Items</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select which order items are included in this fulfillment.
        </p>
        
        <div
          className="border rounded-md divide-y divide-gray-200"
        >
          {orderItemsData.map((item) => {
            const lineItemForm = form.withOrderItems.find(withOrderItem => withOrderItem.orderItemId === Number(item.id));
            return (
              <div
                key={item.id}
                className="p-4 flex items-center cursor-pointer hover:bg-gray-100"
                onClick={(e) => {
                  if (e.target instanceof HTMLInputElement) {
                    return;
                  }

                  handleLineItemSelection(item.id);
                }}
              >
                <input
                  type="checkbox"
                  id={`item-${item.id}`}
                  checked={
                    lineItemForm
                      ? true
                      : false
                  }
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-sm mr-3"
                />
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-700">
                    {getVariationDescription(item.adVariation)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Quantity: {item.quantity}
                  </div>
                  {item.orderFulfillments && item.orderFulfillments.length > 0 && (
                    <div className="text-xs text-amber-600 mt-1">
                      Already included in {item.orderFulfillments.length} other fulfillment(s)
                    </div>
                  )}
                </div>
                <div>
                  <TextInput
                    type="number"
                    value={lineItemForm?.quantity || 0}
                    onChange={(e) => handleLineItemQuantityChange(item.id, parseInt(e.target.value))}
                    max={item.quantity}
                  />
                </div>
              </div>
            )
          })}
          
          {(!orderItemsData || orderItemsData.length === 0) && (
            <div className="text-center py-4 text-gray-500">No order items found</div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default BuyFulFillmentModal;

const BUY_FULFILLMENT_MUTATION = gql`
  mutation BuyFulFillmentModal_BuyOrderFulfillment($input: BuyOrderFulfillmentInput!) {
    buyOrderFulfillment(input: $input) {
      process {
        id
        result
        resultType
      }
    }
  }
`;


const BUY_FULFILLMENT_MODAL_GET_ORDER_QUERY = gql`
  query BuyFulFillmentModal_GetOrder($orderId: ID!) {
    order(id: $orderId) {
      id
      shop {
        shopType
      }
      customerOriginalAddressCountryCode
      orderProductsVariations {
        id
        quantity
        adVariation {
          id
          ad {
            title
          }
          variationTypeValues {
            type {
              type
            }
            value
          }
        }
        supplierProductVariation {
          packaging
          supplierName
        }
        orderFulfillments {
          id
          fullfilledPlatform
        }
      }
    }
  }
`;